import type { KinnserSyncStatus } from '../../services/api/types.gen';

/**
 * One agency's row in the Agency Data Fill rail, and the search over them.
 *
 * Pure functions over the status the list route returns, kept apart from the
 * rail and the pane so the rules below are testable without rendering.
 */

export interface AgencyDataFillRow {
  agencyId: string;
  /** The agency's own name where it has one, else the system name. */
  displayName: string;
  /** The agency's own name, or null. Kept so the rail can decide whether to
   *  show the system name beside it. */
  writtenName: string | null;
  systemName: string;
  /** False when Kinnser is off: nothing to read, nothing to fill. */
  kinnserEnabled: boolean;
  /** When Kinnser was last read for this agency, or null if never. */
  fetchedAt: string | null;
  /** Who asked for that read -- 'schedule' for the nightly sync, or an
   *  operator. Null when it has never been read. */
  fetchSource: string | null;
  /** When that data was last written to the agency's tables, or null. */
  appliedAt: string | null;
  /**
   * The status this row was built from, for the pane.
   *
   * Kept whole rather than flattened field by field: the pane reads JSON
   * columns off it whose shape belongs to the helpers that read them, not to
   * this row.
   */
  status: KinnserSyncStatus;
}

/**
 * Sorted by the name the rows actually show.
 *
 * The route orders by ``system_name``, which is often not what a row displays:
 * AandA's written name is "A & A Home Health". Left alone the rail reads as
 * unordered, and scanning for an agency by eye stops working.
 */
export function agencyDataFillRows(
  statuses: readonly KinnserSyncStatus[]
): AgencyDataFillRow[] {
  return statuses
    .map(status => ({
      agencyId: status.agency_id,
      displayName: status.agency_written_name || status.agency_system_name,
      writtenName: status.agency_written_name ?? null,
      systemName: status.agency_system_name,
      kinnserEnabled: status.kinnser_enabled ?? true,
      fetchedAt: status.fetched_at ?? null,
      fetchSource: status.fetch_source ?? null,
      appliedAt: status.applied_at ?? null,
      status,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export function rowDisplayName(row: AgencyDataFillRow): string {
  return row.displayName;
}

/**
 * The rows a search box shows.
 *
 * Both names are searched, not only the one on display: an operator who knows
 * an agency by its system name should not have to know which of the two the
 * row happens to be showing. Blank search returns everything rather than
 * nothing, so clearing the box is how you get the list back.
 */
export function matchingAgencyDataFillRows(
  rows: readonly AgencyDataFillRow[],
  search: string
): AgencyDataFillRow[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return [...rows];
  return rows.filter(
    row =>
      row.displayName.toLowerCase().includes(needle) ||
      row.systemName.toLowerCase().includes(needle)
  );
}
